import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Check, ShieldCheck, Phone } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useModals } from "@/context/ModalContext";
import { PHONE, PHONE_HREF } from "@/lib/config";
import { SetupCard, setupItems } from "./SetupCard";
import { ComparisonTable } from "./ComparisonTable";
import { CustomizePanel } from "./CustomizePanel";

const Section = ({ title, sub, children, testid }) => (
  <section className="mt-16" data-testid={testid}>
    <h2 className="font-serif text-2xl sm:text-3xl text-cream">{title}</h2>
    {sub && <p className="text-cream/60 mt-2 max-w-2xl">{sub}</p>}
    <div className="mt-7">{children}</div>
  </section>
);

function BestMatch({ setup, onAdd, onCustomize, onExpert, onQuote }) {
  const items = setupItems(setup);
  return (
    <div className="grid lg:grid-cols-2 gap-8 bg-charcoal border border-gold/60 rounded-2xl overflow-hidden" data-testid="best-match">
      <div className="relative min-h-[280px]">
        <img src={setup.heroImage} alt={setup.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 lg:bg-gradient-to-r lg:from-transparent lg:to-charcoal/90" />
        <span className="absolute top-4 left-4 bg-gold text-espresso text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
          Your Best Match
        </span>
      </div>
      <div className="p-6 lg:p-8 lg:pl-0">
        <h3 className="font-serif text-3xl text-cream">{setup.name}</h3>
        <p className="text-cream/70 mt-3 leading-relaxed">{setup.why}</p>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mt-5">
          {items.map((it, i) => (
            <Link key={i} to={`/product/${it.p.handle}`} className="flex items-center gap-2 group">
              <Check size={14} className="text-gold shrink-0" />
              <span className="text-cream/80 text-sm truncate group-hover:text-gold transition-colors">
                <span className="text-gold/70">{it.role}:</span> {it.p.title}
              </span>
            </Link>
          ))}
        </div>
        {setup.compatibilityNote && (
          <p className="text-cream/45 text-xs mt-4 italic border-l-2 border-gold/30 pl-2">{setup.compatibilityNote}</p>
        )}
        <div className="flex flex-wrap gap-3 mt-6">
          <button data-testid="best-request-quote" onClick={() => onQuote(setup)}
            className="bg-gold text-espresso px-6 py-3 rounded-md font-medium hover:bg-amber transition-colors">
            Request a Quote
          </button>
          <button data-testid="best-add-cart" onClick={() => onAdd(setup)}
            className="border border-gold/50 text-gold px-5 py-3 rounded-md hover:bg-gold/10 transition-colors">
            Add Full Setup to Cart
          </button>
          <button data-testid="best-customize" onClick={() => onCustomize(setup)}
            className="border border-white/15 text-cream/80 px-5 py-3 rounded-md hover:border-cream transition-colors">
            Customize Setup
          </button>
          <button data-testid="best-expert" onClick={onExpert}
            className="border border-white/15 text-cream/80 px-5 py-3 rounded-md hover:border-cream transition-colors">
            Talk to a Sauna Expert
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RecoveryResults({ results, answers, onRestart }) {
  const { addItems } = useCart();
  const { openConsultation, openQuote } = useModals();
  const [customizing, setCustomizing] = useState(null);
  const { bestMatch, alternatives = [], curatedSetups = [], comparison = [] } = results;

  const addSetup = (setup) => addItems(setup.lineItems, { openDrawer: true });
  const onExpert = () => openConsultation("From Recovery Builder");
  const quoteSetup = (setup) => {
    const items = setupItems(setup).map((it) => it.p.title).join(", ");
    openQuote(`${setup.name} — ${items}`);
  };

  if (!bestMatch) {
    return (
      <div className="pt-28 pb-20 text-center min-h-screen bg-espresso" data-testid="recovery-results">
        <p className="text-cream/70">We couldn't build a setup from those answers.</p>
        <button onClick={onRestart} className="text-gold underline mt-4">Start over</button>
      </div>
    );
  }

  return (
    <div className="pt-20 pb-24 min-h-screen bg-espresso" data-testid="recovery-results">
      <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="overline flex items-center gap-2">
            <Sparkles size={13} /> {results.aiPowered ? "AI-Designed For You" : "Your Personalised Proposal"}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl text-cream mt-2">Your Recovery Setup</h1>
          <p className="text-cream/60 mt-3 max-w-2xl">
            Based on your space, heat style, capacity, and recovery goals, these are the setups we would recommend.
          </p>
        </motion.div>

        <div className="mt-10">
          <BestMatch setup={bestMatch} onAdd={addSetup} onCustomize={setCustomizing} onExpert={onExpert} onQuote={quoteSetup} />
        </div>

        <Section title="Why This Setup Fits" testid="why-section">
          <div className="bg-charcoal border border-white/10 rounded-xl p-6">
            <p className="text-cream/80 leading-relaxed">{results.whyText}</p>
          </div>
        </Section>

        {alternatives.length > 0 && (
          <Section title="Other Strong Options"
            sub="Curated alternatives that also match your space and goals." testid="alternatives-section">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {alternatives.map((s) => (
                <SetupCard key={s.id} setup={s} onAddToCart={addSetup}
                  onCustomize={setCustomizing} onRequestQuote={quoteSetup} />
              ))}
            </div>
          </Section>
        )}

        {curatedSetups.length > 0 && (
          <Section title="Specific Curated Setups"
            sub="Complete, named setups designed around your answers." testid="curated-section">
            <div className="grid sm:grid-cols-2 gap-5">
              {curatedSetups.map((s) => (
                <SetupCard key={s.id} setup={s} featured onAddToCart={addSetup}
                  onCustomize={setCustomizing} onRequestQuote={quoteSetup} />
              ))}
            </div>
          </Section>
        )}

        {comparison.length > 1 && (
          <Section title="Compare Your Options"
            sub="A clear side-by-side of your recommended setups." testid="compare-section">
            <ComparisonTable rows={comparison} />
          </Section>
        )}

        <Section title="White-Glove Support" testid="white-glove-section">
          <div className="bg-charcoal border border-white/10 rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
            <div className="flex items-start gap-4">
              <ShieldCheck className="text-gold shrink-0 mt-1" size={28} />
              <div>
                <h3 className="font-serif text-xl text-cream">Delivered & installed by experts</h3>
                <p className="text-cream/65 text-sm mt-1 max-w-xl">
                  Our concierge team handles delivery, placement and installation planning so your recovery space arrives ready to enjoy.
                </p>
              </div>
            </div>
            <div className="flex gap-3 shrink-0">
              <button onClick={onExpert} data-testid="wg-consult"
                className="bg-gold text-espresso px-5 py-2.5 rounded-md font-medium hover:bg-amber transition-colors">
                Book a Consultation
              </button>
              <a href={PHONE_HREF} data-testid="wg-call"
                className="flex items-center gap-2 border border-white/15 text-cream/80 px-5 py-2.5 rounded-md hover:border-cream transition-colors">
                <Phone size={15} /> {PHONE}
              </a>
            </div>
          </div>
        </Section>

        <div className="text-center mt-14">
          <button onClick={onRestart} data-testid="builder-restart"
            className="text-cream/60 hover:text-gold text-sm underline">Rebuild my setup</button>
        </div>
      </div>

      <CustomizePanel setup={customizing} open={!!customizing}
        onClose={() => setCustomizing(null)}
        onConfirm={(lineItems) => { addItems(lineItems, { openDrawer: true }); setCustomizing(null); }}
        onRequestQuote={(purpose) => { openQuote(purpose); setCustomizing(null); }} />
    </div>
  );
}
